/**
 * Projects API Client
 *
 * Handles all API calls related to projects and tokens
 */

import { apiClient, ApiResponse } from './client';

// ==================== Types ====================

export interface ProjectTag {
  id: string;
  name: string;
  slug: string;
  sort_order?: number;
}

export interface TokenMarketData {
  id: string;
  currency_code: string;
  issuer_address: string;
  display_name: string | null;
  sort_order: number;

  // Price data
  price_xrp: number | null;
  price_change_24h: number | null;
  market_cap: number | null;
  market_cap_usd?: number | null; // Market cap in USD
  total_supply: number | null;
  has_liquidity: boolean;

  // AMM data
  amm_exists?: boolean;
  amm_liquidity_xrp?: number | null;
  amm_liquidity_token?: number | null;
  amm_liquidity_usd?: number | null; // Total pool value in USD (both sides)
  amm_trading_fee?: number | null; // In basis points (e.g., 500 = 0.5%)

  // Social metrics
  holder_count?: number | null;
  trustline_count?: number | null;
  volume_24h_xrp?: number | null;

  // XRP/USD price for conversions
  xrp_usd_price?: number | null;

  last_updated: string | null;
}

export interface ProjectCollection {
  id: string;
  name: string;
  slug: string | null;
  image_url: string | null;
  floor_price: number | null;
  total_supply: number;
  creator_address: string;
  taxon: number | null;
}

export interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  website_url: string | null;
  twitter_url: string | null;
  discord_url: string | null;
  image_url: string | null;
  banner_url: string | null;
  owner_address: string | null;
  created_at: string;
  updated_at: string;
}

// Announcement types
export interface ProjectAnnouncement {
  id: string;
  content: string;
  created_at: string;
  recipient_count: number | null;
}

export interface ProjectAnnouncementFull extends ProjectAnnouncement {
  is_public: boolean;
  status: 'pending' | 'processing' | 'sent' | 'failed';
  total_holders_at_send: number | null;
  eligible_users_at_send: number | null;
  processed_at: string | null;
  error_message: string | null;
}

export interface AnnouncementStats {
  total_holders: number;
  eligible_users: number;
  can_send_today: boolean;
  last_announcement_at: string | null;
}

export interface ProjectWithDetails extends Project {
  tags?: ProjectTag[];
  tokens?: TokenMarketData[];
  collections?: ProjectCollection[];
  collection_count?: number;
  total_floor_value?: number;
  enabled_features?: string[];
  isManager?: boolean;
}

// Project richlist types
export interface ProjectRichlistCollection {
  id: string;
  name: string;
  slug: string | null;
  image_url: string | null;
}

export interface ProjectRichlistHolderCollection {
  collection_id: string;
  nft_count: number;
  total_points: number;
}

export interface ProjectRichlistHolderEntry {
  rank: number;
  owner_address: string;
  total_nft_count: number;
  total_points: number;
  collections: ProjectRichlistHolderCollection[];
}

export interface ProjectRichlistResult {
  collections: ProjectRichlistCollection[];
  holders: ProjectRichlistHolderEntry[];
  total_holders: number;
  total_points: number;
}

export interface ProjectListItem extends Project {
  tags?: ProjectTag[];
  collection_count?: number;
  total_floor_value?: number;
  total_nft_count?: number;
  total_volume_24h?: number;
  total_volume?: number;
  token_count?: number;
  primary_token?: {
    currency_code: string;
    display_name: string | null;
    price_xrp: number | null;
    price_change_24h: number | null;
    market_cap: number | null;
    market_cap_usd: number | null;
    has_liquidity: boolean;
    holder_count: number | null;
    amm_liquidity_xrp: number | null;
    amm_liquidity_usd: number | null;
    volume_24h_xrp: number | null;
  };
}

export interface ProjectSearchParams {
  search?: string;
  tags?: string[];
  sortBy?: 'alphabetical' | 'market_cap' | 'floor_value' | 'collection_count' | 'recent';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ProjectListResponse {
  projects: ProjectListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ==================== API Functions ====================

/**
 * Search and list projects with filtering and pagination
 */
export async function getProjects(params: ProjectSearchParams = {}): Promise<ProjectListResponse> {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.set('search', params.search);
  if (params.tags && params.tags.length > 0) searchParams.set('tags', params.tags.join(','));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const endpoint = `/projects${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<ProjectListResponse>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch projects');
  }

  return response.data;
}

/**
 * Get a single project by slug with all details
 */
export async function getProjectBySlug(slug: string): Promise<ProjectWithDetails> {
  const response = await apiClient.get<ApiResponse<ProjectWithDetails>>(`/projects/${slug}`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch project');
  }

  return response.data;
}

/**
 * Get live token data for a project
 */
export async function getProjectTokens(slug: string): Promise<TokenMarketData[]> {
  const response = await apiClient.get<ApiResponse<{ tokens: TokenMarketData[] }>>(`/projects/${slug}/tokens`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch project tokens');
  }

  return response.data.tokens;
}

/**
 * Get all available project tags
 */
export async function getProjectTags(): Promise<ProjectTag[]> {
  const response = await apiClient.get<ApiResponse<{ tags: ProjectTag[] }>>('/projects/tags');

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch tags');
  }

  return response.data.tags;
}

// ==================== Announcement API Functions ====================

interface AnnouncementListResponse {
  announcements: ProjectAnnouncement[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

interface AnnouncementFullListResponse {
  announcements: ProjectAnnouncementFull[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
}

/**
 * Get public announcements for a project (anyone can view)
 */
export async function getPublicAnnouncements(
  slug: string,
  limit = 20,
  offset = 0
): Promise<AnnouncementListResponse> {
  const response = await apiClient.get<ApiResponse<AnnouncementListResponse>>(
    `/projects/${slug}/announcements?limit=${limit}&offset=${offset}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch announcements');
  }

  return response.data;
}

/**
 * Get all announcements including private (owner only)
 */
export async function getAllAnnouncements(
  slug: string,
  limit = 20,
  offset = 0
): Promise<AnnouncementFullListResponse> {
  const response = await apiClient.get<ApiResponse<AnnouncementFullListResponse>>(
    `/projects/${slug}/announcements/all?limit=${limit}&offset=${offset}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch announcements');
  }

  return response.data;
}

/**
 * Get announcement stats for a project (owner only)
 */
export async function getAnnouncementStats(slug: string): Promise<AnnouncementStats> {
  const response = await apiClient.get<ApiResponse<AnnouncementStats>>(
    `/projects/${slug}/announcements/stats`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch announcement stats');
  }

  return response.data;
}

/**
 * Create a new announcement (owner only)
 */
export async function createAnnouncement(
  slug: string,
  content: string,
  isPublic = true
): Promise<ProjectAnnouncementFull> {
  const response = await apiClient.post<ApiResponse<ProjectAnnouncementFull>>(
    `/projects/${slug}/announcements`,
    { content, is_public: isPublic }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create announcement');
  }

  return response.data;
}

// ==================== Project Richlist API Functions ====================

/**
 * Get project-wide richlist aggregating points across all collections
 * Returns null if the feature is not enabled for the project (404 response)
 */
export async function getProjectRichlist(slug: string): Promise<ProjectRichlistResult | null> {
  try {
    const response = await apiClient.get<ApiResponse<ProjectRichlistResult>>(
      `/projects/${slug}/richlist`
    );

    if (!response.success || !response.data) {
      return null;
    }

    return response.data;
  } catch (error) {
    // If 404, feature is not enabled - return null instead of throwing
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}
